/* eslint-disable react/jsx-props-no-spreading */
import React, { Children, cloneElement, useMemo } from 'react';
import {
  Breadcrumb as UiLibBreadcrumb, BreadcrumbProps, Icon,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as GatsbyLink } from 'gatsby';
import useSystemPages from '../hooks/useSystemPages';

type LPBreadcrumbItemProps<T extends boolean = false> = React.ComponentPropsWithoutRef<T extends true ? 'span' : typeof GatsbyLink> & {
  isCurrentPage?: T;
  position?: number;
  key?: string;
  to?: string;
};

const BreadcrumbItem = <T extends boolean = false>({
  isCurrentPage,
  children,
  position,
  key,
  to,
  ...rest
}: LPBreadcrumbItemProps<T>,
) => {
  const Component: React.ElementType = isCurrentPage ? 'span' : GatsbyLink;

  return (
    <UiLibBreadcrumb.Item key={key} as="div" isCurrentPage={isCurrentPage} itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
      <Component to={to} itemProp={!isCurrentPage ? 'item' : undefined} {...rest}>
        <span itemProp="name">{children}</span>
      </Component>
      <meta itemProp="position" content={String(position)} />
    </UiLibBreadcrumb.Item>
  );
};

const Breadcrumb = ({
  children,
  className,
  ...rest
}: Omit<BreadcrumbProps, 'aria-label' | 'children' | 'key'> & {
  children: React.ReactElement<LPBreadcrumbItemProps> | React.ReactElement<LPBreadcrumbItemProps>[]
}) => {
  const [home] = useSystemPages('home');
  const homePath = home.path;
  const intl = useIntl();

  const childNodes = useMemo(() => {
    const nodes: typeof children = [];
    Children.forEach(children, (child) => {
      nodes.push(child);
    });
    return nodes;
  }, [children]);

  return (
    <div className={className}>
      <UiLibBreadcrumb
        aria-label={intl.formatMessage({ id: 'general.breadcrumbs.label', defaultMessage: 'Breadcrumb' })}
        itemScope
        itemType="https://schema.org/BreadcrumbList"
        {...rest}
      >
        <UiLibBreadcrumb.Item as="div" isCurrentPage={false} itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
          <GatsbyLink to={homePath} itemProp="item">
            <span className="pd-sr-only md:pd-not-sr-only" itemProp="name">
              <FormattedMessage id="breadcrumb.home" defaultMessage="Home" />
            </span>
            <Icon icon="home" className="md:pd-hidden" />
          </GatsbyLink>
          <meta itemProp="position" content="1" />
        </UiLibBreadcrumb.Item>
        {childNodes.map((child, index) => cloneElement(child, {
          // index + 2: 1 for 0-based index and 1 for Home being always position 1
          position: index + 2,
          key: child.key || `breadcrumb-item-${index}`,
        }))}
      </UiLibBreadcrumb>
    </div>
  );
};

Breadcrumb.Item = BreadcrumbItem;

export default Breadcrumb;
